/**
 * Phased marketing cash schedule — learning → proven → scale.
 * Not Meta Ads (founder banned from Meta Ad platform); model Google/Reddit/LinkedIn
 * + organic social as the spend mix. Cash line drives burn; linked paid uses same $.
 */

export const DEFAULT_MARKETING_PHASES = [
  {
    throughMonth: 6,
    budgetMo: 120,
    label: 'Learning (Google/Reddit tests + organic social)',
  },
  {
    throughMonth: 18,
    budgetMo: 200,
    label: 'Proven (scale winners; still no Meta Ads)',
  },
  {
    throughMonth: 60,
    budgetMo: 350,
    label: 'Scale (raise only after CAC/payback clears)',
  },
];

/**
 * Keep phase resolution deterministic when a scenario was edited or imported
 * outside the form. The UI presents phases in order, so the model must use the
 * same order even when localStorage contains an older or hand-written payload.
 */
export function normalizeMarketingPhases(phases) {
  const source = Array.isArray(phases) && phases.length ? phases : DEFAULT_MARKETING_PHASES;
  const normalized = source
    .map((phase, index) => {
      const throughMonth = Number(phase?.throughMonth);
      const budgetMo = Number(phase?.budgetMo);
      return {
        throughMonth: Math.min(
          60,
          Math.max(1, Math.floor(Number.isFinite(throughMonth) ? throughMonth : index + 1))
        ),
        budgetMo: Number.isFinite(budgetMo) ? Math.max(0, budgetMo) : 0,
        label: phase?.label || `Phase ${index + 1}`,
      };
    })
    .sort((a, b) => a.throughMonth - b.throughMonth);

  for (let i = 1; i < normalized.length; i += 1) {
    normalized[i].throughMonth = Math.max(
      normalized[i].throughMonth,
      normalized[i - 1].throughMonth
    );
  }

  return normalized;
}

/**
 * @param {object} assumptions
 * @param {number} monthIndex — 0 = Now
 * @returns {number} marketing $ for that month
 */
export function resolveMarketingBudgetMo(assumptions, monthIndex) {
  const phases = assumptions?.marketingPhases;
  if (!phases?.length) {
    const budget = Number(assumptions?.marketingBudgetMo);
    return Number.isFinite(budget) ? Math.max(0, budget) : 0;
  }
  // Month 0 ("Now") uses first phase; thereafter throughMonth is 1-based month after Now
  const m = Math.max(1, monthIndex);
  const phase = normalizeMarketingPhases(phases).find((p) => m <= p.throughMonth);
  const fallback = normalizeMarketingPhases(phases).at(-1);
  const budget = phase?.budgetMo ?? fallback?.budgetMo ?? assumptions?.marketingBudgetMo ?? 0;
  return Number.isFinite(Number(budget)) ? Math.max(0, Number(budget)) : 0;
}

export function cloneDefaultMarketingPhases() {
  return DEFAULT_MARKETING_PHASES.map((p) => ({ ...p }));
}
